import apiClient from "../../api";
import * as React from "react";
import classNames from "classnames";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ProgressBar from "react-bootstrap/ProgressBar";
import { gettext } from "i18n";
import { Alert } from "react-bootstrap";

const STATUS_BADGE_CLASSES: Record<string, string> = {
  complete: "bg-success",
  failed: "bg-danger",
  cancelled: "bg-warning text-dark",
  processing: "bg-primary",
  pending: "bg-secondary",
};

interface JobStatus {
  job_id: string;
  status: "pending" | "processing" | "complete" | "failed" | "cancelled";
  export_type: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  progress?: {
    processed_stations: number;
    total_stations: number;
  };
  filename?: string;
  error?: string;
}

const POLL_INTERVAL_MS = 2000;

interface JobHistoryItem {
  job_id: string;
  status: "pending" | "processing" | "complete" | "failed" | "cancelled";
  export_type: string;
  created_at: string;
  completed_at?: string;
  filename?: string;
  file_size_bytes?: number;
  processed_stations?: number;
  total_stations?: number;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString: string): string => {
  // Append 'Z' to treat the input as UTC, then convert to local timezone
  return new Date(dateString + "Z").toLocaleString();
};

const BulkDataExport = () => {
  const [exportInProgress, setExportInProgress] = React.useState(false);
  const [exportType, setExportType] = React.useState<"excel" | "csv">("excel");
  const [jobStatus, setJobStatus] = React.useState<JobStatus | null>(null);
  const [progressMessage, setProgressMessage] = React.useState<string>("");
  const [jobHistory, setJobHistory] = React.useState<JobHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const fetchJobHistory = React.useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiClient.requestRaw("/export/bulk-export/list");
      if (res.status === 200) {
        const data = await res.json();
        setJobHistory(data.jobs || []);
      }
    } catch (err) {
      console.error("Failed to fetch job history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Load job history on mount
  React.useEffect(() => {
    fetchJobHistory();
  }, [fetchJobHistory]);

  const pollJobStatus = React.useCallback(
    async (jobId: string) => {
      try {
        const res = await apiClient.requestRaw(
          `/export/bulk-export/status/${jobId}`,
        );

        if (res.status >= 400) {
          throw new Error(`Failed to get job status: ${res.status}`);
        }

        const status: JobStatus = await res.json();
        setJobStatus(status);

        if (status.status === "complete") {
          toast.success(gettext("Export completed successfully"), {
            theme: "colored",
          });
          setExportInProgress(false);
          setProgressMessage("");
          // Refresh history to show the completed job
          fetchJobHistory();
        } else if (status.status === "failed") {
          toast.error(status.error || gettext("Export failed"), {
            theme: "colored",
          });
          setExportInProgress(false);
          setProgressMessage("");
          // Refresh history to show the failed job
          fetchJobHistory();
        } else if (status.status === "cancelled") {
          toast.info(gettext("Export was cancelled"), {
            theme: "colored",
          });
          setExportInProgress(false);
          setProgressMessage("");
          // Refresh history to show the cancelled job
          fetchJobHistory();
        } else if (status.status === "processing" && status.progress) {
          const { processed_stations, total_stations } = status.progress;
          setProgressMessage(
            `${gettext("Processing")}: ${processed_stations} / ${total_stations} ${gettext("stations")}`,
          );
          // Continue polling
          setTimeout(() => pollJobStatus(jobId), POLL_INTERVAL_MS);
        } else {
          // Pending or no progress yet
          setProgressMessage(gettext("Waiting to start..."));
          setTimeout(() => pollJobStatus(jobId), POLL_INTERVAL_MS);
        }
      } catch (err) {
        console.error(err);
        toast.error(gettext("Error checking export status"), {
          theme: "colored",
        });
        setExportInProgress(false);
        setProgressMessage("");
      }
    },
    [exportType, fetchJobHistory],
  );

  const createExportHandler =
    (exportType: "csv" | "excel") => async (e: React.FormEvent) => {
      e.preventDefault();
      setExportInProgress(true);
      setJobStatus(null);
      setProgressMessage(gettext("Starting export..."));

      try {
        // Initiate async export
        const res = await apiClient.requestRaw(
          `/export/bulk-export/${exportType}`,
          {
            method: "POST",
          },
        );

        if (res.status >= 400) {
          let errorMessage = gettext("Error starting export");
          const contentType = res.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await res.json();
            errorMessage = errorData.message || errorMessage;
          } else {
            const text = await res.text();
            // Try to extract message from HTML error response
            const match = text.match(/<p>([^<]+)<\/p>/);
            if (match) {
              errorMessage = match[1];
            }
          }
          throw new Error(errorMessage);
        }

        const data = await res.json();

        if (!data.success || !data.job_id) {
          throw new Error(data.message || "Failed to create export job");
        }

        toast.info(gettext("Export job created. Processing..."), {
          theme: "colored",
        });

        // Start polling for status
        pollJobStatus(data.job_id);
      } catch (err) {
        console.error(err);
        const message =
          err instanceof Error ? err.message : gettext("Error starting export");
        toast.error(message, { theme: "colored" });
        setExportInProgress(false);
        setProgressMessage("");
      }
    };

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col lg={10} xl={8}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">Bulk Data Export</h4>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <Alert.Heading>Export All Station Data</Alert.Heading>
                <p>
                  This will export all data from all stations in the
                  organizations you administer. The export includes all
                  historical data from the beginning of time for:
                </p>
                <ul className="mb-0">
                  <li>All channels</li>
                  <li>All formulas</li>
                  <li>All manual/lab data</li>
                </ul>
              </Alert>

              <Alert variant="warning">
                <strong>Warning:</strong> Depending on the amount of data, this
                export may take from a few minutes to several hours to complete.
                You can safely navigate away from this page while the export is
                in progress.
              </Alert>

              {exportInProgress && jobStatus && (
                <Alert variant="info" className="mt-3">
                  <Alert.Heading>{gettext("Export in Progress")}</Alert.Heading>
                  <p className="mb-2">{progressMessage}</p>
                  {jobStatus.progress &&
                    jobStatus.progress.total_stations > 0 && (
                      <ProgressBar
                        now={
                          (jobStatus.progress.processed_stations /
                            jobStatus.progress.total_stations) *
                          100
                        }
                        label={`${Math.round(
                          (jobStatus.progress.processed_stations /
                            jobStatus.progress.total_stations) *
                            100,
                        )}%`}
                      />
                    )}
                </Alert>
              )}

              <Form onSubmit={createExportHandler(exportType)}>
                <Form.Group className="mb-3">
                  <Form.Label>Export Format</Form.Label>
                  <Form.Select
                    value={exportType}
                    onChange={(e) =>
                      setExportType(e.target.value as "csv" | "excel")
                    }
                    disabled={exportInProgress}
                  >
                    <option value="excel">Excel (.xlsx)</option>
                    <option value="csv">CSV (.csv)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {exportType === "excel"
                      ? gettext(
                          "Excel format supports better formatting, but is limited to 1 million rows. Recommended for smaller datasets.",
                        )
                      : gettext(
                          "CSV format can handle larger datasets but does not support formatting. Recommended if you have a very large amount of data.",
                        )}
                  </Form.Text>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={exportInProgress}
                  >
                    {exportInProgress ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {gettext("Exporting...")}
                      </>
                    ) : (
                      `${gettext("Export as")} ${exportType.toUpperCase()}`
                    )}
                  </Button>
                </div>
              </Form>

              {/* Export History */}
              {jobHistory.length > 0 && (
                <div className="mt-4">
                  <h5>{gettext("Recent Exports")}</h5>

                  {/* Desktop Table - hidden on small screens */}
                  <div className="d-none d-sm-block">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th style={{ minWidth: "140px" }}>
                              {gettext("Date")}
                            </th>
                            <th style={{ width: "80px" }}>
                              {gettext("Format")}
                            </th>
                            <th style={{ minWidth: "120px" }}>
                              {gettext("Status")}
                            </th>
                            <th style={{ width: "80px" }}>{gettext("Size")}</th>
                            <th style={{ width: "100px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobHistory.slice(0, 10).map((job) => (
                            <tr key={job.job_id}>
                              <td>{formatDate(job.created_at)}</td>
                              <td>{job.export_type.toUpperCase()}</td>
                              <td>
                                <span
                                  className={classNames(
                                    "badge",
                                    STATUS_BADGE_CLASSES[job.status],
                                  )}
                                >
                                  {job.status}
                                </span>
                                {job.status === "processing" &&
                                  job.total_stations &&
                                  job.total_stations > 0 && (
                                    <small className="d-block text-muted mt-1">
                                      {job.processed_stations || 0} /{" "}
                                      {job.total_stations} {gettext("stations")}
                                    </small>
                                  )}
                              </td>
                              <td>
                                {job.file_size_bytes
                                  ? formatFileSize(job.file_size_bytes)
                                  : "-"}
                              </td>
                              <td>
                                {job.status === "complete" && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const res = await apiClient.requestRaw(
                                          `/export/bulk-export/download-url/${job.job_id}`,
                                        );
                                        if (res.status === 200) {
                                          const data = await res.json();
                                          if (data.download_url) {
                                            window.open(
                                              data.download_url,
                                              "_blank",
                                            );
                                          } else {
                                            toast.error(
                                              gettext(
                                                "Download URL not available",
                                              ),
                                              {
                                                theme: "colored",
                                              },
                                            );
                                          }
                                        } else {
                                          const errorData = await res.json();
                                          toast.error(
                                            errorData.message ||
                                              gettext(
                                                "Failed to get download URL",
                                              ),
                                            { theme: "colored" },
                                          );
                                        }
                                      } catch (err) {
                                        console.error(err);
                                        toast.error(
                                          gettext("Error getting download URL"),
                                          {
                                            theme: "colored",
                                          },
                                        );
                                      }
                                    }}
                                  >
                                    {gettext("Download")}
                                  </Button>
                                )}
                                {(job.status === "pending" ||
                                  job.status === "processing") && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const res = await apiClient.requestRaw(
                                          `/export/bulk-export/cancel/${job.job_id}`,
                                          { method: "POST" },
                                        );
                                        if (res.status === 200) {
                                          toast.success(
                                            gettext("Export cancelled"),
                                            {
                                              theme: "colored",
                                            },
                                          );
                                          fetchJobHistory();
                                        } else {
                                          const data = await res.json();
                                          toast.error(
                                            data.message ||
                                              gettext(
                                                "Failed to cancel export",
                                              ),
                                            { theme: "colored" },
                                          );
                                        }
                                      } catch (err) {
                                        console.error(err);
                                        toast.error(
                                          gettext("Error cancelling export"),
                                          {
                                            theme: "colored",
                                          },
                                        );
                                      }
                                    }}
                                  >
                                    {gettext("Cancel")}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards - shown only on small screens */}
                  <div className="d-sm-none">
                    {jobHistory.slice(0, 10).map((job) => (
                      <div
                        key={job.job_id}
                        className="card mb-2 border"
                        style={{ fontSize: "0.875rem" }}
                      >
                        <div className="card-body p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div
                              className="text-truncate"
                              style={{ maxWidth: "60%" }}
                            >
                              <small className="text-muted">
                                {formatDate(job.created_at)}
                              </small>
                            </div>
                            <span
                              className={classNames(
                                "badge",
                                STATUS_BADGE_CLASSES[job.status],
                              )}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {job.export_type.toUpperCase()}
                            </span>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <span
                                className={classNames(
                                  "badge",
                                  STATUS_BADGE_CLASSES[job.status],
                                )}
                              >
                                {job.status}
                              </span>
                              {job.status === "processing" &&
                                job.total_stations &&
                                job.total_stations > 0 && (
                                  <small className="d-block text-muted mt-1">
                                    {job.processed_stations || 0} /{" "}
                                    {job.total_stations}
                                  </small>
                                )}
                            </div>

                            <div className="text-end">
                              {job.file_size_bytes && (
                                <small className="text-muted d-block">
                                  {formatFileSize(job.file_size_bytes)}
                                </small>
                              )}
                              {job.status === "complete" && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="mt-1"
                                  style={{
                                    fontSize: "0.75rem",
                                    padding: "0.25rem 0.5rem",
                                  }}
                                  onClick={async () => {
                                    try {
                                      const res = await apiClient.requestRaw(
                                        `/export/bulk-export/download-url/${job.job_id}`,
                                      );
                                      if (res.status === 200) {
                                        const data = await res.json();
                                        if (data.download_url) {
                                          window.open(
                                            data.download_url,
                                            "_blank",
                                          );
                                        } else {
                                          toast.error(
                                            gettext(
                                              "Download URL not available",
                                            ),
                                            {
                                              theme: "colored",
                                            },
                                          );
                                        }
                                      } else {
                                        const errorData = await res.json();
                                        toast.error(
                                          errorData.message ||
                                            gettext(
                                              "Failed to get download URL",
                                            ),
                                          { theme: "colored" },
                                        );
                                      }
                                    } catch (err) {
                                      console.error(err);
                                      toast.error(
                                        gettext("Error getting download URL"),
                                        {
                                          theme: "colored",
                                        },
                                      );
                                    }
                                  }}
                                >
                                  {gettext("Download")}
                                </Button>
                              )}
                              {(job.status === "pending" ||
                                job.status === "processing") && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="mt-1"
                                  style={{
                                    fontSize: "0.75rem",
                                    padding: "0.25rem 0.5rem",
                                  }}
                                  onClick={async () => {
                                    try {
                                      const res = await apiClient.requestRaw(
                                        `/export/bulk-export/cancel/${job.job_id}`,
                                        { method: "POST" },
                                      );
                                      if (res.status === 200) {
                                        toast.success(
                                          gettext("Export cancelled"),
                                          {
                                            theme: "colored",
                                          },
                                        );
                                        fetchJobHistory();
                                      } else {
                                        const data = await res.json();
                                        toast.error(
                                          data.message ||
                                            gettext("Failed to cancel export"),
                                          { theme: "colored" },
                                        );
                                      }
                                    } catch (err) {
                                      console.error(err);
                                      toast.error(
                                        gettext("Error cancelling export"),
                                        {
                                          theme: "colored",
                                        },
                                      );
                                    }
                                  }}
                                >
                                  {gettext("Cancel")}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BulkDataExport;
